import type {
  AppOptimizationStudyResult,
  AppPosition,
  CommonSettingsData,
  OptimizationStudyTrial,
  RunResult,
  WinLossValueType,
} from "@src/types";
import type {
  ApiStudy,
  ApiStudyTrialMetrics,
  BacktestMetrics,
} from "@tradingdroid/frontends-types";

import { formatDateTimeUTC, normalizeDateTimeForForm } from "./dateUtils";
import { getPositionNetProfit, getPositionProfitPct } from "./positionProfit";
import {
  MISSING_RECORDED_COLLATERAL,
  RECORDED_COLLATERAL_INTERPRETATION,
  UNREPORTED_ACCOUNTING_COVERAGE,
  getTrackingEndedMovementTime,
  hasOnlyTrackingEndedPositions,
  isPositionFinanciallyClosed,
  isPositionTrackingEnded,
} from "@src/utils/positionStatus";
import { minBy } from "lodash-es";

/**
 * Sort object keys alphabetically for consistent display
 * Useful for comparing configs side-by-side (e.g., optimization trials)
 * Returns Record<string, unknown> since key ordering doesn't preserve type structure
 */
export const sortObjectKeys = (obj: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(obj).sort(([a], [b]) => a.localeCompare(b)));

type ExternalFormData = CommonSettingsData & { config: string };

/**
 * Formats external form data (from Redux) for use in the runner form.
 * Normalizes ISO timestamps to datetime-local format (YYYY-MM-DDTHH:mm).
 */
export const formatBacktestDataFromSource = (data: ExternalFormData): ExternalFormData => ({
  ...data,
  startDateTime: normalizeDateTimeForForm(data.startDateTime),
  endDateTime: normalizeDateTimeForForm(data.endDateTime),
});

export const formatDecimal = (value: number | undefined, decimals = 2): string => {
  if (value === undefined) return "N/A";
  return value.toFixed(decimals);
};

/**
 * Format number using browser locale with thousand separators
 * EU locales use dots for thousands and comma for decimals (e.g., 1.234,56)
 * US locales use commas for thousands and period for decimals (e.g., 1,234.56)
 */
export const formatLocaleNumber = (value: number, decimals: number): string => {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatPercentage = (value: number | undefined, decimals = 2): string => {
  if (value === undefined) return "N/A";
  return `${formatDecimal(value * 100, decimals)}%`;
};

/**
 * Parse numeric value from formatted string (handles "123.45 USDT", "1.23%", "$1,234.56", etc.)
 * Extracts a valid number with optional single leading minus and at most one decimal point
 */
export const parseNumericValue = (val: string): number => {
  const trimmed = val.trim();
  // Match optional leading minus, digits, optional decimal with fractional digits
  const match = trimmed.match(/^-?\d+(?:\.\d+)?|(?<=-?)\d+(?:\.\d+)?/);
  if (!match) {
    // Fallback: extract first valid number pattern from anywhere in string
    const fallbackMatch = trimmed.match(/-?\d+(?:\.\d+)?/);
    if (fallbackMatch) {
      const parsed = parseFloat(fallbackMatch[0]);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }
  const parsed = parseFloat(match[0]);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Get default decimal places for a value type
 * flat: 0 (integers), currency/usdt: 3, others: 2
 */
export const getDefaultDecimals = (type: WinLossValueType): number => {
  if (type === "flat") return 0;
  if (type === "currency" || type === "usdt") return 3;
  return 2;
};

// Format value with type-specific suffix (uses locale-aware formatting)
export const formatValueWithType = (
  value: number,
  type: WinLossValueType,
  decimals: number,
): string => {
  const formatted = formatLocaleNumber(value, decimals);
  switch (type) {
    case "currency":
      return `$${formatted}`;
    case "percentage":
      return `${formatted}%`;
    case "usdt":
      return `${formatted} USDT`;
    default:
      return formatted;
  }
};

// Get color class based on threshold or forced color
// Returns empty string (no color) when neither forceColor nor threshold is provided
export const getColorClass = (
  value: number,
  threshold?: number,
  forceColor?: "win" | "loss",
): string => {
  // forceColor takes precedence
  if (forceColor) {
    return forceColor === "win" ? "text-win" : "text-loss";
  }

  // Only apply threshold-based coloring when threshold is explicitly set
  if (threshold !== undefined) {
    return value >= threshold ? "text-win" : "text-loss";
  }

  // No color when neither forceColor nor threshold is provided
  return "";
};

/**
 * Transform ApiStudy (from /v1/studies endpoint) to AppOptimizationStudyResult format
 * Computes aggregated metrics across all trials for display
 *
 * Returns null if study doesn't have full data (trials with user_attrs)
 * List API returns minimal data - must fetch detail endpoint first
 */
export const hydrateApiStudy = (apiStudy: ApiStudy): AppOptimizationStudyResult | null => {
  // Check if study has full data (trials array with user_attrs)
  // List API returns minimal data (study_id, study_name only)
  // Detail API returns full data (direction, finished_trials, best_trial, trials with user_attrs)
  if (
    !apiStudy.trials ||
    !Array.isArray(apiStudy.trials) ||
    apiStudy.trials.length === 0 ||
    !apiStudy.trials[0].user_attrs
  ) {
    return null;
  }

  // Backend returns user_attrs with all metrics
  // Map to expected format with metrics extracted from user_attrs
  const allTrials = apiStudy.trials.map((trial, index) => {
    // Destructure config objects out so metrics only contains metric fields
    const {
      strategy_config: _sc,
      exit_config: _ec,
      regime_config: _rc,
      cost_config: _cc,
      fixed_strategy_params: _fsp,
      fixed_exit_params: _fep,
      ...metrics
    } = trial.user_attrs;

    return {
      ...trial,
      rank: index + 1, // Simple sequential rank for display
      strategy_config: trial.user_attrs.strategy_config || trial.params,
      exit_config: trial.user_attrs.exit_config || {},
      cost_config: trial.user_attrs.cost_config,
      regime_config: trial.user_attrs.regime_config,
      metrics: metrics as ApiStudyTrialMetrics, // All remaining user_attrs fields are metrics
    };
  }) as OptimizationStudyTrial[];

  // Backend provides best_trial - use it directly to avoid expensive calculation
  // Only fallback to first trial if best_trial is missing (shouldn't happen)
  const apiNumBest = apiStudy.best_trial?.trial_number;
  const best_trial =
    apiNumBest !== undefined
      ? allTrials.find((t) => t.trial_number === apiNumBest) || allTrials[0]
      : allTrials[0];

  // Extract metric arrays for aggregation
  const metrics = allTrials.map((t) => t.metrics);
  const cumulative_returns = metrics.map((m) => m.cumulative_returns);
  const win_rates = metrics.map((m) => m.win_rate);
  const profit_factors = metrics.map((m) => m.profit_factor);
  const sortino_ratios = metrics.map((m) => m.sortino_ratio);
  const sharpe_ratios = metrics.map((m) => m.sharpe_ratio);

  // Normalize direction (backend returns "MAXIMIZE" but we want lowercase)
  const direction = apiStudy.direction?.toLowerCase() as "maximize" | "minimize";

  // Return API study + computed hydration fields
  return {
    study_id: apiStudy.study_id,
    study_name: apiStudy.study_name,
    study_url: `https://optuna.tradingdroid.net/dashboard/studies/${apiStudy.study_id}`,
    // Include job metadata if present
    created_ts: apiStudy.job?.create_ts,
    completed_ts: apiStudy.job?.complete_ts,
    from_ts: apiStudy.job?.from_ts,
    end_ts: apiStudy.job?.end_ts,
    // Computed summary fields
    summary: {
      // The study's actual objective; fall back to a direction-based guess for old cached studies
      // whose job config isn't embedded.
      optimization_metric:
        (apiStudy.job?.config as { objective?: string } | undefined)?.objective ??
        (direction === "maximize" ? "cumulative_returns" : "max_drawdown"),
      best_trial_number: best_trial.trial_number,
      best_trial_value: best_trial.value,
      metrics_summary: {
        best_cumulative_returns: Math.max(...cumulative_returns),
        best_win_rate: Math.max(...win_rates),
        best_profit_factor: Math.max(...profit_factors),
        best_sortino_ratio: Math.max(...sortino_ratios),
        best_sharpe_ratio: Math.max(...sharpe_ratios),
        average_win_rate: win_rates.reduce((sum, val) => sum + val, 0) / win_rates.length,
        average_returns:
          cumulative_returns.reduce((sum, val) => sum + val, 0) / cumulative_returns.length,
        total_trials_run: apiStudy.finished_trials!,
        trials_shown: allTrials.length,
      },
      default_configuration: apiStudy.best_trial!.params || {},
    },
    robustness: apiStudy.robustness,
    best_trial,
    best_trials: allTrials,
    all_trials: allTrials,
  };
};

/**
 * Extract relevant metadata from job config based on job type
 * Returns a clean object with only the important fields for display
 */
export const getJobMetadata = (
  jobType: string,
  jobConfig: string | object,
  additionalFields?: {
    exchange_name?: string | null;
    symbol_name?: string | null;
    period?: string | null;
    from_ts?: string | null;
    end_ts?: string | null;
    result?: string | object | null;
    error_message?: string | null;
  },
): Record<string, unknown> => {
  // Parse config if it's a string
  let config: Record<string, unknown> = {};
  if (typeof jobConfig === "string") {
    try {
      config = JSON.parse(jobConfig);
    } catch {
      config = {};
    }
  } else {
    config = jobConfig as Record<string, unknown>;
  }

  switch (jobType) {
    case "backtest": {
      // Extract portfolio config (new format has nested portfolio object)
      const portfolio = (config.portfolio || {}) as Record<string, unknown>;
      const metadata: Record<string, unknown> = {
        // Trading parameters
        exchange: additionalFields?.exchange_name,
        symbol: additionalFields?.symbol_name,
        timeframe: additionalFields?.period,
        date_range: {
          from: additionalFields?.from_ts,
          to: additionalFields?.end_ts,
        },
        initial_capital: portfolio.initial_capital,

        // Strategy configuration (new names: strategy_config, exit_config)
        strategy_config: config.strategy_config,
        exit_config: config.exit_config,

        // Backtest settings (cost_config with portfolio fallback for old cached data)
        apply_fees:
          (config.cost_config as Record<string, unknown>)?.apply_fees ?? portfolio.apply_fees,
        apply_slippage:
          (config.cost_config as Record<string, unknown>)?.apply_slippage ??
          portfolio.apply_slippage,
        cumulative_investment: portfolio.cumulative_investment,
      };

      // Add result metrics if completed
      if (additionalFields?.result) {
        const result =
          typeof additionalFields.result === "string"
            ? JSON.parse(additionalFields.result)
            : additionalFields.result;

        if (result?.success && result?.data?.metrics) {
          metadata.metrics = result.data.metrics;
        }
      }

      return metadata;
    }

    case "live": {
      // Extract portfolio config (new format has nested portfolio object)
      const portfolio = (config.portfolio || {}) as Record<string, unknown>;
      return {
        // Trading parameters
        exchange: additionalFields?.exchange_name,
        symbol: additionalFields?.symbol_name,
        timeframe: additionalFields?.period,
        initial_capital: portfolio.initial_capital,

        // Live execution specific
        exchange_environment: config.exchange_environment || config.environment,
        infisical_key_path: config.infisical_key_path,
        infisical_secret_path: config.infisical_secret_path,
        infisical_password_path: config.infisical_password_path,

        // Strategy configuration (new names: strategy_config, exit_config)
        strategy_config: config.strategy_config,
        exit_config: config.exit_config,
        cumulative_investment: portfolio.cumulative_investment,
      };
    }

    case "optimization": {
      // Extract portfolio config (new format has nested portfolio object)
      const portfolio = (config.portfolio || {}) as Record<string, unknown>;
      const metadata: Record<string, unknown> = {
        // Trading parameters
        exchange: additionalFields?.exchange_name,
        symbol: additionalFields?.symbol_name,
        timeframe: additionalFields?.period,
        date_range: {
          from: additionalFields?.from_ts,
          to: additionalFields?.end_ts,
        },
        initial_capital: portfolio.initial_capital,

        // Optimization settings
        n_trials: config.n_trials,
        timeout: config.timeout,
        objective: config.objective,

        // Backtest settings (cost_config with portfolio fallback for old cached data)
        apply_fees:
          (config.cost_config as Record<string, unknown>)?.apply_fees ?? portfolio.apply_fees,
        apply_slippage:
          (config.cost_config as Record<string, unknown>)?.apply_slippage ??
          portfolio.apply_slippage,
        cumulative_investment: portfolio.cumulative_investment,
      };

      // Add result if completed
      if (additionalFields?.result) {
        const result =
          typeof additionalFields.result === "string"
            ? JSON.parse(additionalFields.result)
            : additionalFields.result;

        if (result?.success && result?.data) {
          metadata.best_trial = result.data.best_trial;
          metadata.study_name = result.data.study_name;
        }
      }

      return metadata;
    }

    case "cache": {
      const metadata: Record<string, unknown> = {
        execution_id: config.execution_id,
      };

      // Add cache URL if completed
      if (additionalFields?.result) {
        const result =
          typeof additionalFields.result === "string"
            ? JSON.parse(additionalFields.result)
            : additionalFields.result;

        if (result?.success && result?.data?.cache_url) {
          metadata.cache_url = result.data.cache_url;
          metadata.execution_time = result.execution_time;
        }
      }

      return metadata;
    }

    default:
      // Generic fallback - return parsed config
      return config;
  }
};

/**
 * Get a human-readable summary for a job (for list view)
 */
// Frontend-facing labels for job types whose UI name differs from the backend job_type_name.
// monte_carlo is presented as "Robustness" everywhere in the UI (see the backtest Robustness tab);
// the wire value stays "monte_carlo". Other types fall back to their capitalized name.
const JOB_TYPE_DISPLAY_LABELS: Record<string, string> = {
  monte_carlo: "Robustness",
};

export const getJobTypeLabel = (jobType: string): string =>
  JOB_TYPE_DISPLAY_LABELS[jobType] ?? jobType.charAt(0).toUpperCase() + jobType.slice(1);

export const getJobSummary = (
  jobType: string,
  exchangeName?: string | null,
  symbolName?: string | null,
  period?: string | null,
): string => {
  const parts: string[] = [];

  if (exchangeName) parts.push(exchangeName.toUpperCase());
  if (symbolName) parts.push(symbolName);
  if (period) parts.push(period);

  if (parts.length === 0) {
    return getJobTypeLabel(jobType);
  }

  return parts.join(" • ");
};

/**
 * Position movement data for copy/export
 */
export interface PositionMovementData {
  type: string;
  dateTime: string;
  price: string;
  contracts: string;
  size: string;
  leverage: string;
  fees: string;
  profit?: string;
  profitPercent?: string;
  duration?: string;
}

/**
 * Position summary data (for both open and closed positions)
 */
export interface PositionSummary {
  status: "WIN" | "LOSS" | "RUNNING" | "TRACKING_ENDED";
  type: string; // long/short
  duration: string;
  size: string;
  leverage: number;
  collateral: string;
  fees: string;
  profit?: string | null; // Closed profit, or null when tracking ended
  profit_pct?: string | null; // Closed profit percentage, or null when tracking ended
}

/**
 * Position data with movements for copy/export
 */
export interface PositionData {
  positionId: string;
  status_name?: "tracking_ended";
  summary: PositionSummary; // Always present: RUNNING for open, WIN/LOSS for closed, TRACKING_ENDED when tracking ended
  movements: PositionMovementData[];
}

/**
 * Run summary statistics for the entire backtest/execution
 */
export interface RunSummary {
  run_type: "backtest" | "execution";
  status: "running" | "finished";
  start_time: string;
  end_time?: string; // undefined if still running
  total_positions: number;
  win_positions: number;
  loss_positions: number;
  win_rate: number | null; // percentage
  total_profit: number | null; // USDT
  total_profit_pct: number | null; // percentage
  avg_profit: number | null; // USDT per position
  total_fees: number; // USDT
  avg_position_size: number; // USDT
  initial_capital?: number | null; // USDT
  final_capital?: number | null; // USDT
  apy?: number; // Annual Percentage Yield (percentage)
  tracking_ended_positions?: number;
  tracking_ended_positions_count?: number | null;
  incomplete_accounting?: boolean | null;
  tracking_ended_collateral_quote?: number | null;
  tracking_ended_collateral_quote_currency?: string | null;
  tracking_ended_collateral_interpretation?: string;
}

/**
 * Complete export structure with run summary and positions
 */
export interface RunExport {
  runSummary: RunSummary;
  positions: PositionData[];
}

/**
 * Format a single position as JSON for clipboard
 */
export const formatPositionAsJSON = (positionData: PositionData): string => {
  return JSON.stringify(positionData, null, 2);
};

/**
 * Format run export (summary + positions) as JSON for clipboard
 */
export const formatRunExportAsJSON = (runExport: RunExport): string => {
  return JSON.stringify(runExport, null, 2);
};

/**
 * Table row interface for position movements (from RunPositions component)
 */
export interface TableRow {
  id: number;
  position: string;
  type: string;
  dateTime: string;
  duration: string;
  price: string;
  contracts: string;
  positionSize: string;
  leverage: string;
  collateral: string;
  fees: string;
  profit: string;
  profitPercent: string;
  cumProfit: string;
  cumProfitPercent: string;
  [key: string]: string | number;
}

/**
 * Safely calculate percentage value with division by zero protection
 *
 * NOTE: This is likely not needed anymore since the backend is now way more robust
 * than it used to be. Consider removing if no edge cases are encountered.
 */
export const safeCalculatePercentage = (
  value: number,
  entryPrice: number,
  contracts: number,
): number => {
  try {
    if (entryPrice === 0 || contracts === 0) return 0;
    const denominator = entryPrice * contracts;
    if (denominator === 0) return 0;
    return (value / denominator) * 100;
  } catch {
    return 0;
  }
};

/**
 * Format duration in seconds between two optional timestamps.
 * Used for quick duration display next to job timestamps.
 */
export const formatDurationSeconds = (startTs?: string | null, endTs?: string | null): string => {
  if (!startTs) return "Not started";

  const startDate = new Date(startTs);
  const endDate = endTs ? new Date(endTs) : new Date();
  const durationSec = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);

  return `${durationSec}s`;
};

/**
 * Calculate duration between two timestamps in human-readable format
 */
export const calculateDuration = (startTs: string, endTs: string): string => {
  try {
    const start = new Date(startTs.replace("Z", "+00:00"));
    const end = new Date(endTs.replace("Z", "+00:00"));
    const deltaMs = end.getTime() - start.getTime();

    const days = Math.floor(deltaMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((deltaMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((deltaMs % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.length > 0 ? parts.join(" ") : "0m";
  } catch {
    return "Unknown";
  }
};

/**
 * Calculate position summary for both open and closed positions
 */
export const calculatePositionSummary = (pos: AppPosition): PositionSummary => {
  const totalFees = pos.total_fees_usdt || 0;
  const posSize = pos.entry_price * pos.entry_amount;

  if (isPositionTrackingEnded(pos)) {
    return {
      status: "TRACKING_ENDED",
      type: pos.direction_name,
      duration: "Unknown",
      size: posSize.toFixed(4),
      leverage: pos.leverage || 1,
      collateral: pos.collateral_in?.toFixed(4) ?? "-",
      fees: pos.total_fees_usdt?.toFixed(4) ?? "-",
      profit: null,
      profit_pct: null,
    };
  }

  // Check if position is closed
  const isClosed = pos.end_ts != null;

  if (isClosed) {
    // Use profit utility (with client-side fallback for execution positions)
    const profit = getPositionNetProfit(pos)!;
    const profitPct = getPositionProfitPct(pos)!;

    // Calculate duration
    const duration = calculateDuration(pos.start_ts, pos.end_ts!);

    return {
      status: (profit > 0 ? "WIN" : "LOSS") as "WIN" | "LOSS",
      type: pos.direction_name,
      duration: duration,
      size: posSize.toFixed(4),
      leverage: pos.leverage || 1,
      collateral: pos.collateral_in?.toFixed(4) || "0",
      fees: totalFees.toFixed(4),
      profit: profit.toFixed(4),
      profit_pct: `${profitPct.toFixed(2)}%`,
    };
  }

  // Open position - show running status with current data
  // Calculate duration from start_ts to now
  const now = new Date().toISOString();
  const duration = pos.start_ts ? calculateDuration(pos.start_ts, now) : "Unknown";

  return {
    status: "RUNNING",
    type: pos.direction_name,
    duration: duration,
    size: posSize.toFixed(4),
    leverage: pos.leverage || 1,
    collateral: pos.collateral_in?.toFixed(4) || "0",
    fees: totalFees.toFixed(4),
    // No profit/profit_pct for running positions
  };
};

/**
 * Position row interface for open/close pairs
 */
export type PositionRow = {
  positionId: string;
  position: AppPosition; // Include full position object to avoid prop drilling
} & (
  | { positionOnly: true; open?: never; close?: never; movements?: never }
  | { positionOnly?: false; open?: TableRow; close?: TableRow; movements?: TableRow[] }
);

/**
 * Open/close pairs by movement order: the first movement opens, the last closes a closed position.
 */
export const buildPositionDisplayRows = (
  positions: AppPosition[],
  tableRows: TableRow[],
): PositionRow[] => {
  const positionGroups = new Map<string, TableRow[]>();
  for (const row of tableRows) {
    const movements = positionGroups.get(row.position) ?? [];
    movements.push(row);
    positionGroups.set(row.position, movements);
  }
  return positions.flatMap((position): PositionRow[] => {
    const positionId = position.id.toString();
    const movements = positionGroups.get(positionId) ?? [];
    if (movements.length === 0) {
      return isPositionTrackingEnded(position)
        ? [{ positionId, position, positionOnly: true }]
        : [];
    }
    return [
      {
        positionId,
        position, // Include full position object
        movements,
        open: movements[0],
        close:
          isPositionFinanciallyClosed(position) && movements.length > 1
            ? movements[movements.length - 1]
            : undefined,
      },
    ];
  });
};

/**
 * Build single position export data from position row
 * Position is now included in the row, eliminating need to search through array
 */
export const buildPositionExport = (row: PositionRow): PositionData => {
  // Position is already in the row - no need to search!
  // Calculate the summary for the position's current tracking state.
  const summary = calculatePositionSummary(row.position);

  const positionData: PositionData = {
    positionId: row.positionId,
    summary,
    movements: [],
  };

  if (isPositionTrackingEnded(row.position)) {
    positionData.status_name = "tracking_ended";
    positionData.movements = row.positionOnly
      ? []
      : (row.movements ?? [row.open, row.close].filter((movement) => movement != null)).map(
          (movement) => {
            const fact = row.position.movements?.find((entry) => entry.id === movement.id);
            return {
              type: movement.type,
              dateTime: fact
                ? formatDateTimeUTC(getTrackingEndedMovementTime(fact))
                : movement.dateTime,
              price: movement.price,
              contracts: movement.contracts,
              size: movement.positionSize,
              leverage: movement.leverage,
              fees: movement.fees,
            };
          },
        );
    return positionData;
  }

  if (row.open) {
    positionData.movements.push({
      type: "Open",
      dateTime: row.open.dateTime,
      price: row.open.price,
      contracts: row.open.contracts,
      size: row.open.positionSize,
      leverage: row.open.leverage,
      fees: row.open.fees,
    });
  }

  if (row.close) {
    positionData.movements.push({
      type: "Close",
      dateTime: row.close.dateTime,
      price: row.close.price,
      contracts: row.close.contracts,
      size: row.close.positionSize,
      leverage: row.close.leverage,
      fees: row.close.fees,
      profit: row.close.profit,
      profitPercent: row.close.profitPercent,
      duration: row.close.duration,
    });
  }

  return positionData;
};

/**
 * Build complete run export data from run, positions, and table rows
 * This encapsulates all the logic for creating the export structure
 */
export const buildRunExport = (
  runResult: RunResult,
  type: "backtest" | "execution",
  positions: AppPosition[],
  tableRows: TableRow[],
  coverageMetrics: BacktestMetrics | null | undefined = runResult.metrics,
): RunExport => {
  // Calculate run summary statistics using profit utility (with client-side fallback)
  const financialPositions = positions.filter((p) => !isPositionTrackingEnded(p));
  const onlyTrackingEnded = hasOnlyTrackingEndedPositions(positions);
  const completedPos = financialPositions.filter((p) => p.end_ts !== null);

  const wins = completedPos.filter((p) => getPositionNetProfit(p)! > 0).length;
  const losses = completedPos.filter((p) => getPositionNetProfit(p)! <= 0).length;
  const totalPnL = completedPos.reduce((sum, p) => sum + getPositionNetProfit(p)!, 0);
  const totalFeesCalc = positions.reduce((sum, p) => sum + (p.total_fees_usdt || 0), 0);
  const avgPosSize =
    positions.length > 0
      ? positions.reduce((sum, p) => sum + p.entry_price * p.entry_amount, 0) / positions.length
      : 0;
  const capitalPosition = minBy(financialPositions, (position) => position.id);
  const initialCap = capitalPosition?.collateral_in ? capitalPosition.collateral_in : 1000;

  // Determine run status: running if execution without end_ts
  const isRunning = type === "execution" && !runResult.end_ts;
  const status: "running" | "finished" = isRunning ? "running" : "finished";

  const apy = runResult.metrics?.annualized_return_pct ?? 0;

  const runSummary: RunSummary = {
    run_type: type,
    status,
    start_time: runResult.start_ts ? formatDateTimeUTC(runResult.start_ts) : "Unknown",
    end_time: runResult.end_ts ? formatDateTimeUTC(runResult.end_ts) : undefined,
    total_positions: financialPositions.length,
    win_positions: wins,
    loss_positions: losses,
    win_rate: onlyTrackingEnded
      ? null
      : completedPos.length > 0
        ? (wins / completedPos.length) * 100
        : 0,
    total_profit: onlyTrackingEnded ? null : totalPnL,
    total_profit_pct: onlyTrackingEnded ? null : initialCap ? (totalPnL / initialCap) * 100 : 0,
    avg_profit: onlyTrackingEnded
      ? null
      : completedPos.length > 0
        ? totalPnL / completedPos.length
        : 0,
    total_fees: totalFeesCalc,
    avg_position_size: avgPosSize,
    initial_capital: onlyTrackingEnded ? null : initialCap,
    final_capital: onlyTrackingEnded ? null : initialCap + totalPnL,
    apy: apy && apy !== 0 ? apy : undefined,
  };

  const trackingEndedCount = positions.filter(isPositionTrackingEnded).length;
  if (trackingEndedCount > 0 || typeof coverageMetrics?.incomplete_accounting === "boolean") {
    runSummary.tracking_ended_positions = trackingEndedCount;
    runSummary.tracking_ended_positions_count =
      coverageMetrics?.tracking_ended_positions_count ?? null;
    runSummary.incomplete_accounting = coverageMetrics?.incomplete_accounting ?? null;
    runSummary.tracking_ended_collateral_quote =
      coverageMetrics?.tracking_ended_collateral_quote ?? null;
    runSummary.tracking_ended_collateral_quote_currency = runResult.symbol?.quote ?? null;
    runSummary.tracking_ended_collateral_interpretation =
      typeof coverageMetrics?.incomplete_accounting !== "boolean"
        ? UNREPORTED_ACCOUNTING_COVERAGE
        : runSummary.tracking_ended_collateral_quote == null
          ? MISSING_RECORDED_COLLATERAL
          : RECORDED_COLLATERAL_INTERPRETATION;
  }

  // Pair movements by row order, as the Positions table does: a futures short opens with a buy.
  const positionsRows = buildPositionDisplayRows(positions, tableRows);

  // Build JSON array of positions using helper
  const allPositions: PositionData[] = positionsRows.map((row) => buildPositionExport(row));

  // Create complete run export with summary and positions
  return {
    runSummary,
    positions: allPositions,
  };
};
